import React, { useCallback, useEffect, useState } from "react";
import { useStore } from "react-admin";
import { DatePicker } from "@mui/x-date-pickers";
import {
    Link,
    MenuItem,
    Paper,
    Skeleton,
    TableBody,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
} from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import moment from "moment";

import BookByDishService from "../../services/BookByDishService";
import CityService from "../../services/CityService";
import CityResponse from "../../interfaces/City/CityResponse";
import { ChefPartyTimeEnum } from "../../enums/ChefForPartyTimeEnums";
import {
    CustomTableRowTR,
    FilterContainer,
    FilterInput,
    FilterSection,
    FilterTextField,
    FilterWrapper,
    FIlterHeader,
    SectionHeader,
    SectionTitle,
    StyledTable,
    TableData,
    TableHeader,
    TableTopHeader,
    TableWrapper,
    Title,
    WorkspaceContainer,
    WorkSpaceTitleRow,
} from "../../styles/commonStyles";
import { FilterIcon } from "../Svg";
import TransactionButton from "../Common/TransactionButton";
import { formatDate } from "../../utils/DateTimeUtils";

const bookByDishService = new BookByDishService();
const cityService = new CityService();
const SKELETON_ROWS = 5;
const COL_COUNT = 13;

const PendingCartsList = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [version] = useStore('ra.ui.viewVersion', 0);

    const [data, setData] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [total, setTotal] = useState(0);
    const [sortOrder, setSortOrder] = useState<"ASC" | "DESC">("DESC");

    // Filter inputs — initialised from URL so they restore on back-navigation
    const [customerPhone, setCustomerPhone] = useState(searchParams.get("phone") ?? "");
    const [cityId, setCityId] = useState(searchParams.get("cityId") ?? "");
    const [date, setDate] = useState<Date | null>(
        searchParams.get("date") ? moment(searchParams.get("date"), "YYYY-MM-DD").toDate() : null
    );
    const [time, setTime] = useState(searchParams.get("time") ?? "");
    const [minPrice, setMinPrice] = useState(searchParams.get("minPrice") ?? "");
    const [maxPrice, setMaxPrice] = useState(searchParams.get("maxPrice") ?? "");

    // Debounced filter values
    const [debouncedPhone, setDebouncedPhone] = useState(searchParams.get("phone") ?? "");
    const [debouncedCityId, setDebouncedCityId] = useState(searchParams.get("cityId") ?? "");
    const [debouncedDate, setDebouncedDate] = useState<Date | null>(
        searchParams.get("date") ? moment(searchParams.get("date"), "YYYY-MM-DD").toDate() : null
    );
    const [debouncedTime, setDebouncedTime] = useState(searchParams.get("time") ?? "");
    const [debouncedMinPrice, setDebouncedMinPrice] = useState(searchParams.get("minPrice") ?? "");
    const [debouncedMaxPrice, setDebouncedMaxPrice] = useState(searchParams.get("maxPrice") ?? "");

    // Serving cities, for the City filter options and to label the City column.
    const [cities, setCities] = useState<CityResponse[]>([]);
    useEffect(() => {
        cityService
            .fetchAllCities(100, 0, { isCurrentlyServing: "YES" }, {})
            .then((res) => setCities(res?.data ?? []))
            .catch(() => setCities([]));
    }, []);

    const cityNameById = React.useMemo(
        () => new Map(cities.map((c) => [c.id, c.name])),
        [cities]
    );

    // Sync filter state → URL (replace so back-button skips intermediate keystrokes)
    useEffect(() => {
        const params: Record<string, string> = {};
        if (customerPhone) params.phone = customerPhone;
        if (cityId) params.cityId = cityId;
        if (date) params.date = moment(date).format("YYYY-MM-DD");
        if (time) params.time = time;
        if (minPrice) params.minPrice = minPrice;
        if (maxPrice) params.maxPrice = maxPrice;
        setSearchParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customerPhone, cityId, date, time, minPrice, maxPrice]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedPhone(customerPhone);
            setDebouncedCityId(cityId);
            setDebouncedDate(date);
            setDebouncedTime(time);
            setDebouncedMinPrice(minPrice);
            setDebouncedMaxPrice(maxPrice);
        }, 600);
        return () => clearTimeout(timer);
    }, [customerPhone, cityId, date, time, minPrice, maxPrice]);

    // Reset to page 0 when filters change
    useEffect(() => {
        setPage(0);
    }, [debouncedPhone, debouncedCityId, debouncedDate, debouncedTime, debouncedMinPrice, debouncedMaxPrice]);

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const filters: any = {
                ...(debouncedPhone && { customerPhone: debouncedPhone }),
                // City is resolved server-side through the cart's area (ck_area.cityId),
                // so unlike time/price below it must not be filtered on the fetched page.
                ...(debouncedCityId && { cityId: Number(debouncedCityId) }),
                ...(debouncedDate && {
                    date: moment(debouncedDate).format("YYYY-MM-DD"),
                }),
            };
            const res = await bookByDishService.fetchAllCarts(
                rowsPerPage,
                page * rowsPerPage,
                filters,
                { field: "id", order: sortOrder }
            );

            // Apply time exact match + price range filter on the fetched data
            const min = debouncedMinPrice !== "" ? Number(debouncedMinPrice) : null;
            const max = debouncedMaxPrice !== "" ? Number(debouncedMaxPrice) : null;
            const timeFilter = debouncedTime.trim();
            const filtered = res.data.filter((row: any) => {
                const price = Number(row?.totalPrice ?? 0);
                if (min !== null && price < min) return false;
                if (max !== null && price > max) return false;
                if (timeFilter && String(row?.time ?? "").trim() !== timeFilter) return false;
                return true;
            });

            const isFiltered = min !== null || max !== null || !!timeFilter;
            setData(filtered);
            setTotal(isFiltered ? filtered.length : res.total);
        } catch {
            setData([]);
            setTotal(0);
        } finally {
            setLoading(false);
        }
    }, [page, rowsPerPage, debouncedPhone, debouncedCityId, debouncedDate, debouncedTime, debouncedMinPrice, debouncedMaxPrice, sortOrder]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    // Re-fetch when the global RA refresh button is clicked
    useEffect(() => {
        if (version > 0) loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [version]);

    const handleClearFilters = () => {
        setCustomerPhone("");
        setCityId("");
        setDate(null);
        setTime("");
        setMinPrice("");
        setMaxPrice("");
    };

    return (
        <WorkspaceContainer style={{ minHeight: '100vh' }}>
            {/* ── Filters ── */}
            <FilterWrapper>
                <FIlterHeader>
                    <FilterSection style={{ marginBottom: "0" }}>
                        <SectionHeader>
                            <WorkSpaceTitleRow>
                                <FilterIcon color="#1976d2" height={20} width={20} />
                                <SectionTitle>Filters</SectionTitle>
                            </WorkSpaceTitleRow>
                        </SectionHeader>
                        <TransactionButton
                            name="Clear Filter"
                            onClickHandler={handleClearFilters}
                        />
                    </FilterSection>
                </FIlterHeader>

                <FilterContainer>
                    <FilterInput
                        value={customerPhone}
                        size="small"
                        variant="outlined"
                        onChange={(e) => setCustomerPhone(e.target.value)}
                        label="Customer Phone"
                        autoComplete="off"
                    />

                    <FilterTextField
                        select
                        value={cityId}
                        size="small"
                        disabled={loading}
                        variant="outlined"
                        onChange={(e) => { setCityId(e.target.value); setPage(0); }}
                        label="City"
                        sx={{ minWidth: 160 }}
                    >
                        <MenuItem value="">All</MenuItem>
                        {cities.map((c) => (
                            <MenuItem key={c.id} value={String(c.id)}>{c.name}</MenuItem>
                        ))}
                    </FilterTextField>

                    <DatePicker
                        label="Date"
                        disableMaskedInput
                        value={date}
                        inputFormat="dd/MM/yyyy"
                        disabled={loading}
                        onChange={(value) => {
                            setDate(value);
                            setPage(0);
                        }}
                        renderInput={(params) => (
                            <FilterTextField {...params} size="small" variant="outlined" />
                        )}
                    />

                    <FilterTextField
                        select
                        value={time}
                        size="small"
                        disabled={loading}
                        variant="outlined"
                        onChange={(e) => { setTime(e.target.value); setPage(0); }}
                        label="Time"
                        sx={{ minWidth: 160 }}
                    >
                        <MenuItem value="">All</MenuItem>
                        {Object.values(ChefPartyTimeEnum).map((t) => (
                            <MenuItem key={t} value={t}>{t}</MenuItem>
                        ))}
                    </FilterTextField>

                    <FilterInput
                        value={minPrice}
                        type="number"
                        size="small"
                        disabled={loading}
                        variant="outlined"
                        onChange={(e) => {
                            const val = e.target.value;
                            if (val === "" || Number(val) >= 0) setMinPrice(val);
                        }}
                        label="Min Price (₹)"
                        InputProps={{ inputProps: { min: 0 } }}
                    />

                    <FilterInput
                        value={maxPrice}
                        type="number"
                        size="small"
                        disabled={loading}
                        variant="outlined"
                        onChange={(e) => {
                            const val = e.target.value;
                            if (val === "" || Number(val) >= 0) setMaxPrice(val);
                        }}
                        label="Max Price (₹)"
                        InputProps={{ inputProps: { min: 0 } }}
                    />
                </FilterContainer>
            </FilterWrapper>

            <br />

            {/* ── Table ── */}
            <TableWrapper>
                <TableTopHeader>
                    <Title>Carts</Title>
                </TableTopHeader>

                <TableContainer component={Paper} sx={{ borderRadius: "8px", "& td, & th": { paddingTop: "18px", paddingBottom: "18px", paddingLeft: "20px", paddingRight: "20px" } }}>
                    <StyledTable sx={{ minWidth: 900 }}>
                        <TableHead>
                            <TableRow
                                sx={{
                                    backgroundColor: "rgba(232, 235, 237, 0.3) !important",
                                }}
                            >
                                <TableHeader>
                                    <TableSortLabel
                                        active
                                        direction={sortOrder === "ASC" ? "asc" : "desc"}
                                        onClick={() =>
                                            setSortOrder((prev) => (prev === "DESC" ? "ASC" : "DESC"))
                                        }
                                    >
                                        Cart ID
                                    </TableSortLabel>
                                </TableHeader>
                                <TableHeader>Customer ID</TableHeader>
                                <TableHeader>Customer Phone</TableHeader>
                                <TableHeader>City</TableHeader>
                                <TableHeader>Date</TableHeader>
                                <TableHeader>Time</TableHeader>
                                <TableHeader>Dishes Price</TableHeader>
                                <TableHeader>Visit Charges</TableHeader>
                                <TableHeader>Total Price</TableHeader>
                                <TableHeader>Total Time</TableHeader>
                                <TableHeader>Items</TableHeader>
                                <TableHeader>Created At</TableHeader>
                                <TableHeader>Checkout Party</TableHeader>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {loading ? (
                                Array.from({ length: SKELETON_ROWS }).map((_, i) => (
                                    <CustomTableRowTR key={i}>
                                        {Array.from({ length: COL_COUNT }).map((__, j) => (
                                            <TableData key={j}>
                                                <Skeleton height={28} />
                                            </TableData>
                                        ))}
                                    </CustomTableRowTR>
                                ))
                            ) : data.length > 0 ? (
                                data.map((row) => (
                                    <CustomTableRowTR
                                        key={row?.id}
                                        onClick={() =>
                                            navigate(
                                                `/ChefForParty/create?type=BOOK_BY_DISH&customerId=${row?.userId}`
                                            )
                                        }
                                        style={{ cursor: "pointer" }}
                                    >
                                        <TableData>{row?.id ?? "-"}</TableData>
                                        <TableData>
                                            <Link
                                                href={`/#/Customers/${row?.userId}/show`}
                                                underline="hover"
                                                color="primary"
                                                onClick={(e: React.MouseEvent) => e.stopPropagation()}
                                            >
                                                {row?.userId ?? "-"}
                                            </Link>
                                        </TableData>
                                        <TableData>{row?.customerPhone ?? "-"}</TableData>
                                        <TableData>
                                            {row?.cityId
                                                ? cityNameById.get(row.cityId) ?? `#${row.cityId}`
                                                : "-"}
                                        </TableData>
                                        <TableData>
                                            {row?.date ? formatDate(row.date) : "-"}
                                        </TableData>
                                        <TableData>{row?.time ?? "-"}</TableData>
                                        <TableData>₹{row?.dishesPrice ?? 0}</TableData>
                                        <TableData>₹{row?.visitCharges ?? 0}</TableData>
                                        <TableData>₹{row?.totalPrice ?? 0}</TableData>
                                        <TableData>{row?.totalTime ?? 0} min</TableData>
                                        <TableData>{row?.items?.length ?? 0}</TableData>
                                        <TableData>
                                            {row?.createdAt
                                                ? moment(row.createdAt).format("DD/MM/YYYY hh:mm A")
                                                : "-"}
                                        </TableData>
                                        <TableData>
                                            {row?.checkoutPartyId ? (
                                                <Link
                                                    href={`/#/ChefForParty/${row.checkoutPartyId}/show`}
                                                    underline="hover"
                                                    onClick={(e: React.MouseEvent) => e.stopPropagation()}
                                                >
                                                    Party #{row.checkoutPartyId}
                                                </Link>
                                            ) : (
                                                "-"
                                            )}
                                        </TableData>
                                    </CustomTableRowTR>
                                ))
                            ) : (
                                <TableRow>
                                    <TableHeader colSpan={COL_COUNT} align="center">
                                        No data available
                                    </TableHeader>
                                </TableRow>
                            )}
                        </TableBody>
                    </StyledTable>
                </TableContainer>
            </TableWrapper>

            {!loading && data.length > 0 && (
                <TablePagination
                    component="div"
                    count={total}
                    page={page}
                    onPageChange={(_, p) => setPage(p)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => {
                        setRowsPerPage(Number(e.target.value));
                        setPage(0);
                    }}
                    rowsPerPageOptions={[10, 25, 50]}
                />
            )}
        </WorkspaceContainer>
    );
};

export default PendingCartsList;
